import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { navLinks, companyInfo } from "../data/data";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? "bg-white/97 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
          : "bg-white"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[76px]">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logo.svg"
              alt="Prayosha Construction"
              className="h-[38px] w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-[13px] font-medium tracking-wide uppercase transition-colors duration-200 ${location.pathname === link.path
                    ? "text-pc-accent"
                    : "text-pc-primary/70 hover:text-pc-primary"
                  }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-pc-accent rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 text-sm font-body text-pc-muted hover:text-pc-primary transition-colors duration-200"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="text-[13px]">{companyInfo.phone}</span>
            </a>
            <Link to="/contact">
              <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-[13px] font-semibold px-6 h-10 rounded-lg transition-colors duration-200">
                Get a Quote
                <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-pc-primary hover:bg-pc-light transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <div className="px-4 pb-6 pt-2 bg-white border-t border-pc-border/50 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${location.pathname === link.path
                  ? "text-pc-accent bg-pc-accent/5"
                  : "text-pc-text hover:text-pc-accent hover:bg-pc-light"
                }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 border-t border-pc-border/50">
            <Link to="/contact" className="block mt-2">
              <Button className="w-full bg-pc-accent hover:bg-pc-accent/90 text-white rounded-lg h-11 font-semibold">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};
